// The store's small pieces that live outside the store pages: the cart button in the site header, the "who am I
// shopping as" bar at the top of store pages, and the account picker with its link-your-account steps.
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import seoPages from "../../../../shared/seo-pages.json";
import { DiscordMark, Icon } from "../../icons";
import { Badge, Skeleton, errorMessage } from "../../ui";
import { PlayerHead } from "../../ui/kit";
import { useSite } from "../context";
import { StoreDialog } from "./StoreDialog";
import { useStore } from "./StoreSession";
import { platformLabel, type StoreAccount } from "./storeSite";

export const signInHref = (returnPath: string) => `/api/v1/auth/login?returnPath=${encodeURIComponent(returnPath)}`;

/* ---------- Header ---------- */

export function CartButton() {
  const store = useStore();
  if (!store.visible || !store.signedIn) return null;
  const count = store.count;
  return <button type="button" className="icon-button cart-button" aria-haspopup="dialog" aria-expanded={store.dialog === "cart"}
    aria-label={count === 0 ? "Cart, empty" : `Cart, ${count} ${count === 1 ? "item" : "items"}`} onClick={store.openCart}>
    <Icon name="cart" size={20}/>
    {count > 0 && <span className="cart-count" aria-hidden="true">{count > 99 ? "99+" : count}</span>}
  </button>;
}

/* ---------- Shopping bar ---------- */

// Says who the page is shopping as and how to change that, or what is missing before anything can be bought.
const StoreLinks = () => <nav className="shopping-links" aria-label="Your store account"><Link to="/store/orders">Orders</Link><Link to="/store/wallet">Wallet</Link></nav>;

export function ShoppingBar({ links = true }: { links?: boolean } = {}) {
  const store = useStore();
  const location = useLocation();
  if (!store.signedIn) {
    return <section className="shopping-bar" aria-label="Shopping account">
      <p>Sign in with Discord to buy and to keep a cart.</p>
      <a className="button discord" href={signInHref(location.pathname + location.search)}><DiscordMark/>Sign in with Discord</a>
    </section>;
  }
  const { accounts, selected } = store;
  if (accounts === null && !selected) return <section className="shopping-bar" aria-label="Shopping account" aria-busy="true"><p className="muted">Checking your Minecraft accounts…</p></section>;
  if (!selected) {
    return <section className="shopping-bar" aria-label="Shopping account">
      <p>Your Discord account has no Minecraft account linked yet.</p>
      <button type="button" className="button primary" onClick={store.openAccount}><Icon name="link" size={16}/>Link your Minecraft account to buy</button>
      {links && <StoreLinks/>}
    </section>;
  }
  return <section className="shopping-bar" aria-label="Shopping account">
    <p className="shopping-as">
      <PlayerHead name={selected.name} size={32}/>
      <span><small>Shopping on</small><strong>{selected.name}</strong></span>
      <Badge tone="neutral">{platformLabel(selected)}</Badge>
    </p>
    {(accounts?.length ?? 0) > 1 && <button type="button" className="button compact" aria-haspopup="dialog" onClick={store.openAccount}>Change account</button>}
    {links && <StoreLinks/>}
  </section>;
}

/* ---------- Account picker ---------- */

export function AccountPicker() {
  const store = useStore();
  const { me } = useSite();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const needAccounts = store.accounts === null;
  const { refreshAccounts } = store;
  useEffect(() => {
    if (needAccounts) refreshAccounts().catch((reason) => setError(errorMessage(reason, "Your accounts could not be loaded. Try again.")));
  }, [needAccounts, refreshAccounts]);
  const accounts = store.accounts;
  const choose = async (account: StoreAccount) => {
    if (account.uuid === store.selected?.uuid) { store.closeDialog(); return; }
    setBusy(account.uuid); setError("");
    try { await store.select(account.uuid); store.closeDialog(); }
    catch (reason) { setError(errorMessage(reason, "That account could not be selected. Try again.")); setBusy(null); }
  };
  const none = accounts !== null && accounts.length === 0;
  return <StoreDialog title={none ? "Link your Minecraft account" : "Which account are you shopping on?"} onClose={store.closeDialog} above>
    {accounts === null && !error && <Skeleton rows={2} label="Loading your accounts"/>}
    {error && <p className="field-error" role="alert">{error}</p>}
    {none && <LinkSteps canUsePortal={me.permissions.includes("portal.access")}/>}
    {accounts !== null && accounts.length > 0 && <>
      <ul className="account-list">{accounts.map((account) => {
        const current = account.uuid === store.selected?.uuid;
        return <li key={account.uuid}><button type="button" className="account-row" aria-current={current ? "true" : undefined} disabled={busy !== null} onClick={() => choose(account)}>
          <PlayerHead name={account.name} size={40}/>
          <span className="account-row-name"><strong>{account.name}</strong><small>{busy === account.uuid ? "Switching…" : current ? "Shopping on this account" : "Shop on this account"}</small></span>
          <span className="account-row-badges"><Badge tone="neutral">{platformLabel(account)}</Badge>{account.primary && <Badge tone="accent">Primary</Badge>}</span>
          {current && <Icon name="check" size={18}/>}
        </button></li>;
      })}</ul>
      <p className="fine-print">Ranks and purchase limits belong to the Minecraft account. Your cart is checked again for the account you pick.</p>
    </>}
  </StoreDialog>;
}

function LinkSteps({ canUsePortal }: { canUsePortal: boolean }) {
  if (!canUsePortal) return <>
    <p>Everything you buy is delivered to a Minecraft account, so one has to be linked first.</p>
    <p>Linking happens in the member portal, which your Discord roles do not include yet. Ask a staff member in the SkyBoxy Discord to help.</p>
  </>;
  return <>
    <p>Everything you buy is delivered to a Minecraft account, so link yours first. It takes a minute.</p>
    <ol className="store-steps">
      <li>Open the <Link to="/discord/portal/minecraft">Minecraft tab of your member portal</Link> and get a one-time code.</li>
      <li>Join <strong>{seoPages.serverAddress}</strong> on the account you want to link.</li>
      <li>Type <code>/link</code> and your code in chat.</li>
    </ol>
    <Link className="button primary" to="/discord/portal/minecraft"><Icon name="link" size={16}/>Open the member portal</Link>
  </>;
}
